/** P1 · 2B — the ONLY frontend interpretation of a job's residual stage.
 *
 * The server (Python ``task_db.residual_state``) is the canonical semantics: it
 * ships a normalized {@link ResidualDisplay} class. This module is the minimal
 * UI mirror — a class → localized text/flags map — and NOTHING else re-derives
 * the domain rules. Because we mirror the OUTPUT classes (not the input
 * ``residual_outcome`` vocabulary), a future 7th outcome that maps to an
 * existing class never touches this file. A cross-language contract test
 * (``tests/unit/test_residual_display_contract.py``) fails if the class SET
 * here diverges from Python.
 */
import type { ResidualDisplay } from '../api/types';

/** The full class set — kept exhaustive against {@link ResidualDisplay} at
 *  compile time via ``satisfies`` and pinned against Python by the contract
 *  test. */
export const RESIDUAL_DISPLAY_CLASSES = [
  'residual',
  'absent',
  'compute_failed',
  'operational_failure',
  'unprocessed',
  'unknown',
] as const satisfies readonly ResidualDisplay[];

export interface ResidualNotice {
  /** Localized notice text, or ``null`` when there is nothing to surface (a
   *  residual exists, or the job simply has not been processed yet). */
  text: string | null;
  /** True ONLY for «нет эталона» — a proven/legacy scientific absence.
   *  Analytics may count these; operational failures and unknowns must NOT be
   *  counted as scientific absence (that regression is the heart of P1). */
  isScientificAbsence: boolean;
  /** Operational / unknown states stay VISIBLE to the operator but are
   *  excluded from scientific aggregates — never silently dropped. */
  isVisibleTechnical: boolean;
}

/** Map a residual render class (+ optional operator-facing failure code) to the
 *  UI notice. Exhaustive: adding a {@link ResidualDisplay} member without a
 *  case here is a compile error. */
export function residualNotice(
  display: ResidualDisplay,
  failureCode?: string | null,
): ResidualNotice {
  switch (display) {
    case 'residual':
    case 'unprocessed':
      return { text: null, isScientificAbsence: false, isVisibleTechnical: false };
    case 'absent':
      return {
        text: 'Эталонные координаты не найдены в IGS Daily SINEX за указанную дату и ±5 дней. Сравнение невозможно.',
        isScientificAbsence: true,
        isVisibleTechnical: false,
      };
    case 'compute_failed':
      return {
        text: 'PPP-сервис не вычислил координаты («Не удалось вычислить координаты»). Сравнение невозможно.',
        isScientificAbsence: false,
        isVisibleTechnical: false,
      };
    case 'operational_failure': {
      const code = failureCode ? ` (${failureCode})` : '';
      return {
        text: `Не удалось получить эталон — техническая ошибка${code}. Сравнение отложено; требуется повтор или действие оператора.`,
        isScientificAbsence: false,
        isVisibleTechnical: true,
      };
    }
    case 'unknown':
      return {
        text: 'Состояние эталона не определено — проверьте задачу.',
        isScientificAbsence: false,
        isVisibleTechnical: true,
      };
    default: {
      const _exhaustive: never = display;
      return _exhaustive;
    }
  }
}
